import { CheckIcon, CircleDashed, X } from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useSharedStatusManagement } from "@/components/ProjectConfiguration/hooks";
import { isDefaultStatusName } from "@/components/ProjectConfiguration/utils/statusNameUtils";
import { validateCustomStatus } from "@/components/ProjectConfiguration/utils/validateCustomStatus";
import { KanbanDefaultStates } from "@/components/ProjectList/CanvasKanban";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import type { Status } from "@/types/status";
import type { Project, ProjectWithStatuses } from "@/utils/types";

interface StatusBadgeProps {
  status?: Status | null;
  className?: string;
  issueId: string;
  statuses?: Status[];
  project?: Project;
  onChange?: (status: Status) => void;
  variant?: "outline" | "default" | "text-dark";
  borderless?: boolean;
}

interface StatusMenuItemProps {
  status: Status;
  isSelected: boolean;
  onStatusChange: (status: Status) => void;
  onClose: () => void;
}

const StatusMenuItem: React.FC<StatusMenuItemProps> = ({
  status,
  isSelected,
  onStatusChange,
  onClose,
}) => {
  const Icon = status.icon;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onStatusChange(status);
    onClose();
  };

  return (
    <DropdownMenuItem onClick={handleClick}>
      <div className="flex items-center justify-between w-full py-1 pl-2 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="shrink-0">
            <Icon />
          </div>
          <span className="truncate max-w-[120px]" title={status.name}>
            {status.name}
          </span>
        </div>
        {isSelected && <CheckIcon className="size-3.5" color="#F5F5F6" />}
      </div>
    </DropdownMenuItem>
  );
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  className = "",
  issueId,
  statuses,
  project,
  onChange,
  variant = "default",
  borderless = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const updateIssueStatus = useIssuesStore((state) => state.updateIssueStatus);
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    } else {
      setSearchQuery("");
    }
  }, [isOpen]);

  const customProjectStatuses = useMemo(() => {
    return (project as ProjectWithStatuses)?.statuses || [];
  }, [project]);

  const { comprehensiveStatusList } = useSharedStatusManagement({
    customProjectStatuses,
  });

  const filteredAllStatuses = useMemo(() => {
    if (!searchQuery.trim()) return comprehensiveStatusList;

    return comprehensiveStatusList.filter((status) =>
      status.name?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [comprehensiveStatusList, searchQuery]);

  const filteredStatuses = project ? filteredAllStatuses : statuses || [];

  const handleStatusChange = async (newStatus: Status) => {
    if (onChange) {
      onChange(newStatus);
      return;
    }

    if (!issueId) {
      toast.error("Could not update status");
      return;
    }

    const originalStatus = status?.name || KanbanDefaultStates.BACKLOG;
    const isDefaultStatus = isDefaultStatusName(newStatus.name);
    try {
      updateIssueStatus(supabaseClient, issueId, newStatus.name);
      toast.success(`Status updated to ${newStatus.name}`);
    } catch (error) {
      console.error("Failed to update status", error);
      toast.error("Failed to update status");
      return;
    }

    // For custom statuses, validate after the update and rollback if needed
    if (!isDefaultStatus && project?.id) {
      const validation = await validateCustomStatus(
        project.id,
        newStatus.name,
        false,
      );

      if (!validation.isValid) {
        // Rollback: Move issue back to original status
        try {
          await updateIssueStatus(supabaseClient, issueId, originalStatus);
          toast.error(
            validation.error ||
              "Cannot update to this status - it has been deleted or updated. Status moved back to previous value.",
          );
        } catch (rollbackError) {
          console.error("Error rolling back status:", rollbackError);
          toast.error(
            "Status validation failed and rollback failed. Please refresh the page.",
          );
        }
      }
    }
  };

  const filteredStatus = filteredStatuses.find((s) => s.name === status?.name);

  const BadgeIcon = filteredStatus?.badgeIcon ?? filteredStatus?.icon;
  const StatusIcon = filteredStatus?.icon;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <div>
          {variant === "text-dark" ? (
            <div
              className={cn(
                `flex items-center justify-center h-7 w-fit p-0 bg-secondary-0 rounded-md hover:bg-secondary-n-800 cursor-pointer`,
                !borderless ? "border border-border-primary" : "",
                className,
              )}
            >
              {StatusIcon ? (
                <div className="flex items-center gap-1 p-1.5 min-w-0">
                  <div className="shrink-0">
                    <StatusIcon />
                  </div>
                  <span
                    className="text-xs font-medium text-text-primary whitespace-nowrap truncate max-w-[100px]"
                    title={status?.name}
                  >
                    {status?.name}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-1 p-1.5">
                  <CircleDashed className="size-4 pl-[3px] text-secondary-n-400" />
                  <span className="text-xs font-medium text-secondary-n-400 whitespace-nowrap">
                    Status
                  </span>
                </div>
              )}
            </div>
          ) : (
            <Badge
              className={cn(
                "h-6 w-6 p-0 flex items-center justify-center",
                "text-muted-foreground hover:bg-secondary-n-800 border-none",
                className,
              )}
            >
              {BadgeIcon && <BadgeIcon />}
            </Badge>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        ref={menuRef}
        className="w-[200px] overflow-hidden bg-secondary-0 rounded-lg scrollbar-none"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          searchInputRef.current?.focus();
        }}
        onPointerDownOutside={(e) => {
          if (!menuRef.current?.contains(e.target as Node)) {
            setIsOpen(false);
          }
        }}
        onInteractOutside={(e) => {
          if (searchInputRef.current?.contains(e.target as Node)) {
            e.preventDefault();
          }
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-1">
          <div className="relative">
            <Input
              ref={searchInputRef}
              placeholder="Change status"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              onKeyUp={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              className="h-7 text-sm bg-secondary-n-950 border-0! border-none! focus:border-0! focus:border-none! focus-visible:border-0! focus-visible:border-none! focus-visible:ring-0! focus-visible:!ring-none placeholder:text-secondary-n-600 pr-6"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        </div>
        <DropdownMenuSeparator />
        <div
          className="overflow-y-auto overflow-x-hidden max-h-[300px] context-menu-scroll"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
          }}
        >
          <div className="pl-4 py-1 caption text-2xs font-medium text-secondary-n-400">
            Statuses
          </div>
          {(project ? filteredAllStatuses : filteredStatuses).map((s) => (
            <StatusMenuItem
              key={s.id}
              status={s}
              isSelected={status?.name === s.name}
              onStatusChange={handleStatusChange}
              onClose={() => setIsOpen(false)}
            />
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
